import type { ImageModel } from '@rocco/ui/tile-shared/components/TileMedia';

/**
 * WebsiteTileViewModel represents an external link/website pin in design boards.
 * It displays a thumbnail with a blurred background effect to fill the container.
 * When using a placeholder image, it falls back to object-cover display.
 */
export interface WebsiteTileViewModel {
    /** Unique identifier for the tile */
    id: number;
    /** Title of the external link (page title or custom title) */
    title: string;
    /** Domain or brand name to display as description */
    description?: string;
    /** The external URL to navigate to */
    link?: string;
    /** Thumbnail image from the external website */
    thumbnail: ImageModel;
    /** Discriminator to identify this as a website tile */
    type: 'website';
    /** Whether there's a real scraped image (not placeholder). Used to determine display style. */
    hasImage: boolean;
    /** Badge label to display (e.g., 'New', 'Featured'). Null means no badge. */
    badge?: string | null;
}

/**
 * Type guard to check if a model is a WebsiteTileViewModel.
 */
export const isWebsiteTileViewModel = (
    model: unknown
): model is WebsiteTileViewModel => {
    return (
        typeof model === 'object' &&
        model !== null &&
        'type' in model &&
        (model as WebsiteTileViewModel).type === 'website'
    );
};
